/**
 * Studio actions — confirmation, credits, backend calls, and simulated fallbacks.
 *
 * The Studio is mixed-mode now. The priced generation-style actions still run through
 * `generationApi.runStudioAction()` and may stage placeholder results, while the audio
 * editing and download paths call `audioEditApi` whenever a backend is configured.
 *
 * The four preserved prompts (extend/remix/replace/cover) are still collected by the
 * forms, held in studioStore, and passed as part of the typed StudioAction so the
 * generation side can consume them when that backend path becomes real.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  audioEditApi,
  buildExportAudioRequest,
  hasStoredAudioFile,
  storedAudioRequirementMessage,
  type StoredStemType,
  type StemAvailabilityResult,
} from '../api/audioEditApi';
import type { TrimRange } from '../lib/trim';
import { coverApi } from '../api/coverApi';
import { ApiError, apiErrorMessage, toApiError } from '../api/errors';
import { generationApi } from '../api/generationApi';
import {
  buildStemFile,
  buildStemsManifest,
  downloadFromUrl,
  downloadTextFile,
} from '../lib/download';
import { STUDIO_COVER_IMAGE_POLICY } from '../lib/coverImagePolicy';
import { readImageAsDataUrl, validateImageFile } from '../lib/imageUpload';
import { STEM_TRACKS } from '../lib/stems';
import {
  STUDIO_ACTION_CREDITS as STUDIO_ACTION_COST,
  type PricedStudioActionKind,
} from '../lib/credits';
import { selectSongById, useSongsStore } from '../stores/songsStore';
import { useCreditsStore } from '../stores/creditsStore';
import { useStudioStore } from '../stores/studioStore';
import { useUiStore } from '../stores/uiStore';
import type { GenerationStatus, Song, StudioActionKind } from '../types/domain';
import { warmGeneratedSongForPlayback } from './usePlayer';
import { USE_MOCK_API } from '../config';

/** Version label and success toast for each action. */
const ACTION_COPY: Record<StudioActionKind, { toast: string }> = {
  extend: { toast: 'Song extended — new version added' },
  remix: { toast: 'Remix completed' },
  replace: { toast: 'Section replaced' },
  trim: { toast: 'Song trimmed' },
  cut: { toast: 'Section cut' },
  split: { toast: 'Song split into two' },
  crossfade: { toast: 'Crossfaded into a new song' },
  insertSilence: { toast: 'Silence inserted' },
  fade: { toast: 'Fades applied' },
  normalize: { toast: 'Section normalized' },
  mix: { toast: 'Mix applied' },
  master: { toast: 'Mastering applied' },
  stems: { toast: 'Stems ready — download them from the drawer' },
  cover: { toast: 'Cover art ready — apply it to use it' },
  regenerate: { toast: 'Song regenerated from your lyrics — new version added' },
};

const STEM_LABELS: Record<StoredStemType, string> = {
  vocals: 'Vocals',
  bass: 'Bass',
  drums: 'Drums',
  guitar: 'Guitar',
  piano: 'Piano',
  other: 'Other',
};

const STORED_STEM_TYPES: StoredStemType[] = ['vocals', 'bass', 'drums', 'guitar', 'piano', 'other'];

export function useStudio() {
  const songId = useStudioStore((s) => s.songId);
  const drawer = useStudioStore((s) => s.drawer);
  const processing = useStudioStore((s) => s.processing);
  const compareIds = useStudioStore((s) => s.compareIds);
  const openStudio = useStudioStore((s) => s.openStudio);
  const closeStudio = useStudioStore((s) => s.closeStudio);
  const setDrawer = useStudioStore((s) => s.setDrawer);
  const setSongId = useStudioStore((s) => s.setSongId);
  const setProcessing = useStudioStore((s) => s.setProcessing);
  const toggleCompare = useStudioStore((s) => s.toggleCompare);
  const clearCompare = useStudioStore((s) => s.clearCompare);
  const dropFromCompare = useStudioStore((s) => s.dropFromCompare);
  const setField = useStudioStore((s) => s.setField);
  const buildAction = useStudioStore((s) => s.buildAction);
  const pendingCover = useStudioStore((s) => s.pendingCover);
  const setPendingCover = useStudioStore((s) => s.setPendingCover);
  const readyStemsSongIds = useStudioStore((s) => s.readyStemsSongIds);
  const markStemsReady = useStudioStore((s) => s.markStemsReady);

  const songs = useSongsStore((s) => s.songs);
  const [stemAvailability, setStemAvailability] = useState<{
    songId: string | null;
    sourceVersionId: string | null;
    jobId: string | null;
    loading: boolean;
    loadFailed: boolean;
    stemTypes: StoredStemType[];
    jobStatus: GenerationStatus | null;
    expectedStemCount: number;
  }>({
    songId: null,
    sourceVersionId: null,
    jobId: null,
    loading: false,
    loadFailed: false,
    stemTypes: [],
    jobStatus: null,
    expectedStemCount: 0,
  });
  const stemAvailabilityRequest = useRef(0);
  const [activeProcessingActionKind, setActiveProcessingActionKind] = useState<PricedStudioActionKind | null>(null);
  const processingActionKind = activeProcessingActionKind;
  const addVersion = useSongsStore((s) => s.addVersion);
  const addSong = useSongsStore((s) => s.addSong);
  const replaceSong = useSongsStore((s) => s.replaceSong);
  const saveVersionToLibraryRaw = useSongsStore((s) => s.saveVersionToLibrary);
  const updateSong = useSongsStore((s) => s.updateSong);
  const deleteVersionRaw = useSongsStore((s) => s.deleteVersion);
  const duplicateVersionRaw = useSongsStore((s) => s.duplicateVersion);
  const renameVersion = useSongsStore((s) => s.renameVersion);
  const loadVersionForEditingRaw = useSongsStore((s) => s.loadVersionForEditing);
  const editingHeadVersionId = useSongsStore((s) => s.editingHeadVersionId);
  const stemSourceVersionId = editingHeadVersionId ?? null;
  const showToast = useUiStore((s) => s.showToast);

  const song: Song | null = selectSongById(songs, songId);
  const editingHeadVersion = song?.versions.find((version) => version.id === editingHeadVersionId) ?? null;
  const storedAudioActionSource = editingHeadVersion ?? song;
  const storedAudioStatusSource = song
    ? { songOrigin: song.songOrigin, audioUrl: storedAudioActionSource?.audioUrl }
    : storedAudioActionSource;
  const [playingStemLabel, setPlayingStemLabel] = useState<string | null>(null);
  const [loadingStemLabel, setLoadingStemLabel] = useState<string | null>(null);
  const stemAudioRef = useRef<HTMLAudioElement | null>(null);
  const backendStemDownloadsAvailable = audioEditApi.supports('exportAudio');
  const availabilityMatchesSong = Boolean(
    song &&
      stemAvailability.songId === song.id &&
      stemAvailability.sourceVersionId === stemSourceVersionId
  );
  const availableStemTypes = availabilityMatchesSong ? stemAvailability.stemTypes : [];
  const stemsLoading = Boolean(
    song && (!availabilityMatchesSong || stemAvailability.loading || stemAvailability.loadFailed)
  );
  const stemsChecked = Boolean(
    song && availabilityMatchesSong && !stemAvailability.loading && !stemAvailability.loadFailed
  );
  const stemsProcessing = Boolean(
    availabilityMatchesSong &&
      (stemAvailability.jobStatus === 'queued' ||
        stemAvailability.jobStatus === 'running' ||
        (stemAvailability.jobStatus === 'succeeded' &&
          stemAvailability.expectedStemCount > availableStemTypes.length))
  );
  const hasCompleteStemSet = Boolean(
    stemAvailability.expectedStemCount > 0 &&
      availableStemTypes.length >= stemAvailability.expectedStemCount
  );
  const stemsReady = Boolean(
    song &&
      (backendStemDownloadsAvailable
        ? (stemAvailability.jobStatus === 'succeeded' && hasCompleteStemSet) ||
          (stemAvailability.jobStatus === null && availableStemTypes.length >= STORED_STEM_TYPES.length)
        : readyStemsSongIds.includes(song.id))
  );
  const downloadableStemLabels =
    availableStemTypes.length > 0
      ? availableStemTypes.map((stemType) => STEM_LABELS[stemType])
      : !backendStemDownloadsAvailable && stemsReady
        ? STORED_STEM_TYPES.map((stemType) => STEM_LABELS[stemType])
        : [];

  function requireStoredAudio(action: string): boolean {
    if (hasStoredAudioFile(storedAudioActionSource)) return true;
    showToast(storedAudioRequirementMessage(storedAudioStatusSource, action));
    return false;
  }

  const applyStemAvailability = useCallback((
    targetSongId: string,
    targetSourceVersionId: string | null,
    availability: StemAvailabilityResult
  ) => {
    setStemAvailability({
      songId: targetSongId,
      sourceVersionId: targetSourceVersionId,
      jobId: availability.job?.id ?? null,
      loading: false,
      loadFailed: false,
      stemTypes: availability.stemTypes,
      jobStatus: availability.job?.status ?? null,
      expectedStemCount: availability.job?.stemNames.length ?? availability.stemTypes.length,
    });
  }, []);

  const refreshAvailableStems = useCallback(async (
    targetSongId: string,
    targetSourceVersionId: string | null = null,
    options?: { showLoading?: boolean }
  ): Promise<StoredStemType[]> => {
    const requestId = ++stemAvailabilityRequest.current;
    if (options?.showLoading !== false) {
      setStemAvailability({
        songId: targetSongId,
        sourceVersionId: targetSourceVersionId,
        jobId: null,
        loading: true,
        loadFailed: false,
        stemTypes: [],
        jobStatus: null,
        expectedStemCount: 0,
      });
    }
    if (!backendStemDownloadsAvailable) {
      setStemAvailability({
        songId: targetSongId,
        sourceVersionId: targetSourceVersionId,
        jobId: null,
        loading: false,
        loadFailed: false,
        stemTypes: [],
        jobStatus: null,
        expectedStemCount: 0,
      });
      return [];
    }
    try {
      const availability = await audioEditApi.getStemAvailability(targetSongId, targetSourceVersionId);
      if (requestId === stemAvailabilityRequest.current) {
        applyStemAvailability(targetSongId, targetSourceVersionId, availability);
      }
      return availability.stemTypes;
    } catch {
      if (requestId === stemAvailabilityRequest.current) {
        setStemAvailability((current) =>
          options?.showLoading === false &&
              current.songId === targetSongId &&
              current.sourceVersionId === targetSourceVersionId
            ? { ...current, loading: false, loadFailed: true }
            : {
                songId: targetSongId,
                sourceVersionId: targetSourceVersionId,
                jobId: null,
                loading: false,
                loadFailed: true,
                stemTypes: [],
                jobStatus: null,
                expectedStemCount: 0,
              }
        );
      }
      return [];
    }
  }, [applyStemAvailability, backendStemDownloadsAvailable]);

  const currentSongId = song?.id ?? null;
  useEffect(() => {
    if (drawer !== 'stems' || !currentSongId) return;
    void refreshAvailableStems(currentSongId, stemSourceVersionId);
  }, [currentSongId, drawer, refreshAvailableStems, stemSourceVersionId]);

  useEffect(() => {
    if (drawer !== 'stems' || !currentSongId) return;
    const refreshAfterReconnect = () => {
      void refreshAvailableStems(currentSongId, stemSourceVersionId);
    };
    window.addEventListener('online', refreshAfterReconnect);
    return () => window.removeEventListener('online', refreshAfterReconnect);
  }, [currentSongId, drawer, refreshAvailableStems, stemSourceVersionId]);

  useEffect(() => {
    if (
      drawer !== 'stems' ||
      !currentSongId ||
      (!stemsProcessing && !stemAvailability.loadFailed) ||
      (processing && activeProcessingActionKind === 'stems')
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      if (!navigator.onLine) return;
      void refreshAvailableStems(currentSongId, stemSourceVersionId, { showLoading: false });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [
    activeProcessingActionKind,
    currentSongId,
    drawer,
    processing,
    refreshAvailableStems,
    stemAvailability.loadFailed,
    stemSourceVersionId,
    stemsProcessing,
  ]);

  async function toggleStemPlayback(stemName: string) {
    if (!song || !backendStemDownloadsAvailable) return;
    if (playingStemLabel === stemName && stemAudioRef.current) {
      stemAudioRef.current.pause();
      stemAudioRef.current = null;
      setPlayingStemLabel(null);
      setLoadingStemLabel(null);
      return;
    }

    const key = STEM_TRACKS.find((stem) => stem.label === stemName)?.id ?? stemName;
    const request = buildExportAudioRequest({
      songId: song.id,
      stemJobId: stemAvailability.jobId ?? undefined,
      format: 'WAV',
      quality: 'lossless',
      content: 'stems',
      stemName: key,
    });

    try {
      setLoadingStemLabel(stemName);
      const result = await audioEditApi.exportAudio(request);
      const previous = stemAudioRef.current;
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = result.url;
      audio.addEventListener(
        'ended',
        () => {
          if (stemAudioRef.current === audio) stemAudioRef.current = null;
          setPlayingStemLabel((current) => (current === stemName ? null : current));
          setLoadingStemLabel((current) => (current === stemName ? null : current));
        },
        { once: true }
      );
      audio.addEventListener(
        'playing',
        () => {
          setLoadingStemLabel((current) => (current === stemName ? null : current));
        },
        { once: true }
      );
      audio.addEventListener(
        'error',
        () => {
          if (stemAudioRef.current === audio) stemAudioRef.current = null;
          setPlayingStemLabel((current) => (current === stemName ? null : current));
          setLoadingStemLabel((current) => (current === stemName ? null : current));
          showToast(`Could not play ${stemName}.`);
        },
        { once: true }
      );
      stemAudioRef.current = audio;
      setPlayingStemLabel(stemName);
      previous?.pause();
      await audio.play();
    } catch (err) {
      if (stemAudioRef.current) {
        stemAudioRef.current.pause();
        stemAudioRef.current = null;
      }
      setPlayingStemLabel(null);
      setLoadingStemLabel(null);
      showToast(apiErrorMessage(err, { preserveMessage: true }));
    }
  }

  useEffect(() => {
    return () => {
      stemAudioRef.current?.pause();
      stemAudioRef.current = null;
      setLoadingStemLabel(null);
      setPlayingStemLabel(null);
    };
  }, []);

  /**
   * Runs a priced Studio action immediately.
   *
   * `lyrics` is only meaningful for `regenerate`, and it is captured here — at the
   * moment the user asked — rather than read again later, so the payload cannot drift
   * from what was on screen when the button was pressed.
   */
  function request(kind: PricedStudioActionKind, _label: string, lyrics?: string) {
    void perform(kind, lyrics);
  }

  async function withApplyReservation<T>(
    kind: PricedStudioActionKind,
    run: () => Promise<T>
  ): Promise<T> {
    const amount = STUDIO_ACTION_COST[kind];
    const reserved = USE_MOCK_API && !useCreditsStore.getState().reserve(amount);
    if (reserved) throw new ApiError(402, 'Not enough credits to apply this Studio action.', 'insufficient_credits');
    try {
      const result = await run();
      if (USE_MOCK_API) useCreditsStore.getState().commitReservation(amount, `Studio — ${kind}`);
      else await useCreditsStore.getState().loadBalance();
      return result;
    } catch (error) {
      if (USE_MOCK_API && toApiError(error).kind !== 'network') {
        useCreditsStore.getState().releaseReservation(amount);
      }
      throw error;
    }
  }

  /**
   * Runs a priced action: charge, process, then record the result.
   *
   * Charging before processing is deliberate — an action must not start if the
   * account cannot pay for it.
   */
  async function perform(kind: PricedStudioActionKind, lyrics?: string) {
    const builtAction = buildAction(kind, lyrics);
    const action = builtAction?.kind === 'stems' && stemSourceVersionId
      ? { ...builtAction, sourceVersionId: stemSourceVersionId }
      : builtAction;
    if (!action || !song) return;
    if (kind === 'stems' && !requireStoredAudio('split this song into stems')) return;
    if (kind === 'stems') {
      setActiveProcessingActionKind(kind);
      setProcessing(true);
      try {
        if (
          await generationApi.resumeExistingStemSplit(
            song.id,
            (availability) => applyStemAvailability(song.id, stemSourceVersionId, availability),
            stemSourceVersionId ?? undefined
          )
        ) {
          markStemsReady(song.id);
          await refreshAvailableStems(song.id, stemSourceVersionId);
          showToast(ACTION_COPY[kind].toast);
          return;
        }
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
        return;
      } finally {
        setProcessing(false);
        setActiveProcessingActionKind(null);
      }
    }
    let spent = false;
    if (USE_MOCK_API) {
      if (!useCreditsStore.getState().reserve(STUDIO_ACTION_COST[kind])) return;
    }
    spent = true;

    setActiveProcessingActionKind(kind);
    setProcessing(true);
    try {
      const result =
        action.kind === 'stems'
          ? await generationApi.runStudioAction(action, {
              onStemAvailability: (availability) =>
                applyStemAvailability(action.songId, stemSourceVersionId, availability),
            })
          : await generationApi.runStudioAction(action);
      if (USE_MOCK_API) {
        useCreditsStore.getState().commitReservation(STUDIO_ACTION_COST[kind], `Studio — ${kind}`);
      } else await useCreditsStore.getState().loadBalance();

      // Version labels mirror the prototype's wording exactly.
      switch (action.kind) {
        case 'extend':
        case 'remix':
        case 'replace':
        case 'trim':
          if ('id' in result) {
            replaceSong(result);
            warmGeneratedSongForPlayback(result);
          }
          break;
        case 'stems':
          // Stems add no version, but a successful split unlocks the per-stem WAV downloads.
          markStemsReady(song.id);
          await refreshAvailableStems(song.id, stemSourceVersionId);
          break;
        case 'cover': {
          /*
            Generation now STAGES the artwork instead of writing it to the song.
            It used to call updateSong() here, which meant an experiment replaced
            a cover the user was happy with and there was no undo. The result goes
            to pendingCover, the preview shows it, and Apply is what commits.

            No version record either, for the same reason: nothing has changed
            about the song yet. CoverPanel's Apply adds it.
          */
          /*
            THE SONG'S OWN GENRE AND MOOD GO IN, not only the panel's two fields. The
            direction the batch asks for is genre + mood + prompt (lib/coverConcept), and
            the panel collects a style and an optional prompt — so the rest comes from the
            record. `action.prompt` first when the user typed one, because a person
            describing what they want is a better subject than the song's original prompt;
            `song.prompt` when they left it blank, so the artwork is still about the song
            rather than about nothing.

            `variant` CHAINS OFF THE ARTWORK ALREADY ON SCREEN, and only here: Regenerate
            means "another take on this", so pressing it twice must not return the same
            gradient twice. Feeding the previous seed in — the staged one if there is one,
            otherwise the song's — makes each press a different result while keeping the
            whole thing deterministic, which a clock or a random number would not.
            Automatic generation passes no variant at all, so the same request always gives
            the same cover; see generationApi.createGeneration.
          */
          const explicitStyle = typeof action.style === 'string' && action.style.trim() ? action.style : undefined;
          const result = await coverApi.generateCover({
            songId: song.id,
            prompt: action.prompt?.trim() || song.prompt,
            genre: song.genre,
            mood: song.mood,
            style: explicitStyle,
            variant: pendingCover?.artworkSeed ?? song.artworkSeed ?? null,
          });
          setPendingCover({
            source: 'generated',
            coverUrl: result.coverUrl,
            artworkSeed: result.artworkSeed,
            coverStyle: result.concept?.style ?? explicitStyle,
          });
          break;
        }
        case 'regenerate':
          /*
            In backend mode regeneration returns the updated Song, including the new
            audio and version entry, so the store is replaced wholesale. The mock has
            no audio pipeline and still returns the action itself, so the local lyrics
            update and version record remain as the fallback.
          */
          if ('id' in result) {
            replaceSong(result);
            warmGeneratedSongForPlayback(result);
          } else {
            updateSong(song.id, {
              lyrics: action.lyrics,
              lyricsTimedRaw: undefined,
              lyricTimings: undefined,
              lyricSectionTimings: undefined,
            });
            addVersion(
              song.id,
              'Regenerated from edited lyrics',
              'regenerate',
              undefined,
              undefined,
              undefined,
              undefined,
              action.lyrics,
              undefined,
              undefined,
              undefined
            );
          }
          break;
      }

      /*
        Cover art is the one action whose result is visible in its own panel, so
        the panel stays open to show it. Every other action produces a version
        record, which lives elsewhere, so closing to the timeline is still right.

        Without this exception the Cover Art preview would be unveiled and then
        immediately unmounted by the same success path.
      */
      if (kind !== 'cover' && kind !== 'stems') setDrawer(null);
      showToast(ACTION_COPY[kind].toast);
    } catch (err) {
      if (USE_MOCK_API && spent && toApiError(err).kind !== 'network') {
        useCreditsStore.getState().releaseReservation(STUDIO_ACTION_COST[kind]);
      }
      showToast(apiErrorMessage(err, { preserveMessage: true }));
    } finally {
      // In `finally` so a failure cannot leave the drawer spinning forever.
      setProcessing(false);
      setActiveProcessingActionKind(null);
    }
  }

  /**
   * Asks the export boundary for one stem, or the whole ZIP, and saves what comes back.
   *
   * One function covers both rows because a single stem and the archive differ only in
   * whether `stemName` is present. When the backend is configured this downloads real
   * WAV/ZIP assets; when it is not, the callers stay on the honest text-placeholder branch
   * below.
   *
   * The format is fixed at WAV. Stems are working files, not share exports.
   */  async function receiveStemExport(target: Song, stemName: string | undefined) {
    /*
      THE PANEL PASSES A LABEL, THE REQUEST CARRIES A KEY, and translating between them is
      this line. `ExportAudioRequest.stemName` is documented as keyed from STEM_TRACKS, and
      the download rows pass `stem.label` ("Drums") because that is also the toast copy and
      part of the placeholder filename. Sending "Drums" where the contract says "drums" would
      be a field that looks right and is wrong — the exact drift an unsent request accumulates.

      Resolved here rather than by changing what the panel passes: the label is what the
      existing placeholder filenames are built from, and renaming those to fix an unreachable
      request would change real output for no reason. An unmatched name falls through as-is,
      which keeps a future sixth stem working rather than silently exporting the whole set.
    */
    const key = STEM_TRACKS.find((stem) => stem.label === stemName)?.id ?? stemName;
    const request = buildExportAudioRequest({
      songId: target.id,
      stemJobId: stemAvailability.jobId ?? undefined,
      format: 'WAV',
      quality: 'lossless',
      content: 'stems',
      stemName: key,
    });
    try {
      const result = await audioEditApi.exportAudio(request);
      downloadFromUrl(result.url, result.filename);
      showToast(stemName ? `Downloading ${stemName}` : 'Downloading stems ZIP');
    } catch (err) {
      // The refusal's own message, which names the missing capability — see api/errors.ts,
      // where UnsupportedCapabilityError maps to 501 with its message preserved.
      showToast(apiErrorMessage(err, { preserveMessage: true }));
    }
  }

  return {
    song,
    songId,
    drawer,
    processing,
    processingActionKind,
    compareIds,
    stemsReady,
    stemsLoading,
    stemsChecked,
    stemsProcessing,
    stemExpectedCount: availabilityMatchesSong ? stemAvailability.expectedStemCount : 0,
    availableStemTypes,
    downloadableStemLabels,
    playingStemLabel,
    loadingStemLabel,
    refreshAvailableStems,
    openStudio,
    closeStudio,
    setDrawer,
    toggleCompare,
    clearCompare,
    setField,
    renameVersion,

    /** Costs, so the tool buttons can show them. */
    costs: STUDIO_ACTION_COST,

    runExtend: () => request('extend', 'Extend song'),
    runRemix: (style?: string | null) => request('remix', style ? `Remix — ${style}` : 'Remix'),
    runReplace: () => request('replace', 'Replace section'),

    async runTrim(range: TrimRange) {
      if (!song || !requireStoredAudio('trim this song')) return;
      setProcessing(true);
      try {
        const trimmed = await withApplyReservation('trim', () => audioEditApi.trim(song.id, range, editingHeadVersionId));
        replaceSong(trimmed);
        warmGeneratedSongForPlayback(trimmed);
        setDrawer(null);
        showToast(ACTION_COPY.trim.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runCut(range: TrimRange) {
      if (!song || !requireStoredAudio('cut this song')) return;
      setProcessing(true);
      try {
        const cut = await withApplyReservation('cut', () => audioEditApi.cut(song.id, range, editingHeadVersionId));
        replaceSong(cut);
        warmGeneratedSongForPlayback(cut);
        // Left open on success: Cross fade is a follow-up edit nested under Cut, so the
        // panel has to stay on screen long enough to offer it.
        showToast(ACTION_COPY.cut.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    /**
     * Splits the open song into two new Version History drafts and returns their backing ids.
     *
     * The visible library row stays put: the returned `song` carries both new split versions,
     * while `firstSong`/`secondSong` are only the hidden backing rows Crossfade needs next.
     */
    async runSplit(atSeconds: number) {
      if (!song || !requireStoredAudio('split this song')) return undefined;
      setProcessing(true);
      try {
        const result = await withApplyReservation('split', () => audioEditApi.split(song.id, Math.round(atSeconds), editingHeadVersionId));
        replaceSong(result.song);
        showToast(ACTION_COPY.split.toast);
        return { firstSongId: result.firstSong.id, secondSongId: result.secondSong.id };
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
        return undefined;
      } finally {
        setProcessing(false);
      }
    },

    /**
     * Joins two songs' audio into a third, new song with a crossfade at the seam —
     * built for Split's two halves. Unlike Split, this DOES become the workspace's
     * loaded HEAD: it is a deliberate "keep going from here" action, not a silent half.
     */
    async runCrossfade(firstSongId: string, secondSongId: string, seconds: number) {
      if (!requireStoredAudio('crossfade this song')) return undefined;
      setProcessing(true);
      try {
        const crossfaded = await withApplyReservation('crossfade', () => audioEditApi.crossfade(firstSongId, secondSongId, seconds));
        addSong(crossfaded);
        warmGeneratedSongForPlayback(crossfaded);
        setSongId(crossfaded.id);
        showToast(ACTION_COPY.crossfade.toast);
        return crossfaded;
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
        return undefined;
      } finally {
        setProcessing(false);
      }
    },

    async runInsertSilence(at: number, duration: number) {
      if (!song || !requireStoredAudio('insert silence into this song')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('insertSilence', () => audioEditApi.insertSilence(song.id, at, duration, editingHeadVersionId));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        showToast(ACTION_COPY.insertSilence.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runFade(seconds: number) {
      if (!song || !requireStoredAudio('fade this song')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('fade', () => audioEditApi.fade(song.id, seconds, editingHeadVersionId));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        showToast(ACTION_COPY.fade.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runNormalize(range: TrimRange, targetLufs: number) {
      if (!song || !requireStoredAudio('normalize this song')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('normalize', () => audioEditApi.normalize(song.id, range, targetLufs, editingHeadVersionId));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        showToast(ACTION_COPY.normalize.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runTempo(toBpm: number) {
      if (!song || !requireStoredAudio('change this song\'s tempo')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('tempo', () => audioEditApi.setTempo({
          songId: song.id,
          fromBpm: song.bpm,
          toBpm,
          preservePitch: true,
          ...(editingHeadVersionId ? { sourceVersionId: editingHeadVersionId } : null),
        }));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        setDrawer(null);
        showToast('Tempo changed');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runPitch(semitones: number) {
      if (!song || !requireStoredAudio('change this song\'s pitch')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('pitch', () => audioEditApi.setPitch({
          songId: song.id,
          semitones,
          preserveTempo: true,
          ...(editingHeadVersionId ? { sourceVersionId: editingHeadVersionId } : null),
        }));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        setDrawer(null);
        showToast('Pitch changed');
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runMix(input: {
      tracks?: Array<{ id: string; volume: number; pan: number; muted: boolean }>;
      eq: { lowBass: number; midBass: number; mids: number; highMids: number; presence: number };
      compressor: {
        attackMs: number;
        releaseMs: number;
        thresholdDb: number;
        ratio: number;
        makeupGainDb: number;
      };
      reverb: {
        delayMs: number;
        feedback: number;
        dryVolume: number;
        wetVolume: number;
      };
      delay: {
        delayMs: number;
        feedback: number;
        mix: number;
      };
    }) {
      if (!song || !requireStoredAudio('mix this song')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('mix', () => audioEditApi.applyMix({
          songId: song.id,
          tracks: input.tracks,
          eq: input.eq,
          compressor: input.compressor,
          reverb: input.reverb,
          delay: input.delay,
          ...(editingHeadVersionId ? { sourceVersionId: editingHeadVersionId } : null),
        }));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        setDrawer(null);
        showToast(ACTION_COPY.mix.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    async runMaster(input: { target: string; targetLufs: number; truePeak: number; character: string }) {
      if (!song || !requireStoredAudio('master this song')) return;
      setProcessing(true);
      try {
        const updated = await withApplyReservation('master', () => audioEditApi.master({
          songId: song.id,
          target: input.target,
          targetLufs: input.targetLufs,
          truePeak: input.truePeak,
          character: input.character,
          ...(editingHeadVersionId ? { sourceVersionId: editingHeadVersionId } : null),
        }));
        replaceSong(updated);
        warmGeneratedSongForPlayback(updated);
        setDrawer(null);
        showToast(ACTION_COPY.master.toast);
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      } finally {
        setProcessing(false);
      }
    },

    runSplitStems: () => request('stems', 'Split stems'),
    runGenerateCover: (style?: string | null) => request('cover', style ? `Cover art — ${style}` : 'Cover art'),

    /**
     * Saves edited lyrics without regenerating.
     *
     * Separate from regeneration on purpose: fixing a typo should not cost credits or
     * queue a render. The stored lyrics are what a later regeneration reads if the
     * user comes back to it, and what Copy/Download/Print export.
     */
    saveLyrics(lyrics: string) {
      if (!song) return;
      updateSong(song.id, {
        lyrics,
        lyricsTimedRaw: undefined,
        lyricTimings: undefined,
        lyricSectionTimings: undefined,
      });
      showToast('Lyrics saved');
    },

    /**
     * Regenerates the song from the lyric sheet currently in the editor.
     *
     * The text is passed in rather than read from the song, because the point is to
     * re-sing what is on screen — including edits not yet saved.
     */
    runRegenerate: (lyrics: string) => request('regenerate', 'Regenerate song', lyrics),

    /** Artwork produced but not yet committed. Null when the song's own art is shown. */
    pendingCover,

    /**
     * Stages an uploaded image for preview.
     *
     * Reads locally and does NOT contact the API: the file is only sent when the user
     * applies it, so an image they look at and reject is never uploaded. Rejections
     * and read failures both surface as a toast and leave any existing staged artwork
     * alone — a bad second pick should not wipe out a good first one.
     */
    async uploadCoverFile(file: File) {
      const rejection = validateImageFile(file, STUDIO_COVER_IMAGE_POLICY);
      if (rejection) {
        showToast(rejection.message);
        return;
      }
      try {
        const dataUrl = await readImageAsDataUrl(file);
        setPendingCover({ source: 'uploaded', coverUrl: dataUrl, filename: file.name });
        showToast('Image ready — apply it to use it');
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Could not read that image file.');
      }
    },

    /**
     * Commits the staged artwork to the song. THE intentional act.
     *
     * Both paths land here, which is why generating and uploading cannot overwrite
     * artwork by themselves. Writing `coverUrl` and `artworkSeed` together — rather
     * than patching one and leaving the other — matters because a song that kept a
     * stale seed alongside a new image would render differently depending on which
     * surface resolved it first.
     */
    async applyPendingCover() {
      if (!song || !pendingCover) return;
      setProcessing(true);
      try {
        /*
          Only the upload path talks to the API — it is the one with bytes to send.
          The generated path has a seed and nothing else until an image model exists.
        */
        const result =
          pendingCover.source === 'uploaded' && pendingCover.coverUrl !== null
            ? await coverApi.uploadCover(song.id, pendingCover.coverUrl)
            : pendingCover.source === 'generated'
              ? { coverUrl: pendingCover.coverUrl, artworkSeed: pendingCover.artworkSeed }
              : { coverUrl: null, artworkSeed: pendingCover.artworkSeed };

        /*
          Both fields are always written, never patched one at a time. A song holding a
          stale seed next to a new image would draw differently depending on which
          surface resolved it, which is the class of bug lib/artwork exists to end.
        */
        updateSong(song.id, {
          coverUrl: result.coverUrl,
          artworkSeed: result.artworkSeed,
          coverStyle: pendingCover.coverStyle,
          coverSource: pendingCover.source,
        });
        addVersion(
          song.id,
          pendingCover.source === 'uploaded' ? 'Cover art uploaded' : 'Cover art applied',
          'cover'
        );
        setPendingCover(null);
        showToast('Cover art applied');
      } catch {
        // The staged artwork is left in place: the user's image is still valid and
        // still previewed, so Apply can simply be pressed again.
        showToast('Could not apply that cover art. Try again.');
      } finally {
        setProcessing(false);
      }
    },

    /** Throws the staged artwork away, leaving the song's current cover untouched. */
    discardPendingCover() {
      setPendingCover(null);
      showToast('Discarded');
    },

    /**
     * Removes the song's cover art.
     *
     * All four artwork fields are cleared in ONE patch, and that is the whole reason
     * this is a hook action rather than an inline updateSong at the call site. Clearing
     * `coverUrl` alone leaves the seed behind, so the song keeps drawing the generated
     * gradient and "remove" appears not to have worked; clearing the seed alone leaves
     * an uploaded image behind. Leaving `coverSource` behind makes the caption claim a
     * source for artwork that no longer exists. The song falls back to a gradient keyed
     * on its id — see lib/artwork.artworkSeedFor — which is the same default state a
     * song has before any artwork is applied, so every surface agrees again.
     *
     * A version record is added rather than not, because removing artwork is a change
     * to the song like applying it is, and Version History is where changes are undone.
     *
     * Also clears any staged artwork: the box would otherwise still be showing a
     * preview with an Apply button, which after a remove reads as if the removal had
     * been reversed.
     *
     * Free. Nothing is generated and nothing is sent, so there is nothing to charge
     * for, and a confirmation dialog for an action Version History can undo would be
     * a dialog for its own sake.
     */
    removeCoverArt() {
      if (!song) return;
      if (!song.coverUrl && !song.artworkSeed) {
        showToast('There is no cover art to remove');
        return;
      }
      updateSong(song.id, {
        coverUrl: null,
        artworkSeed: undefined,
        coverStyle: undefined,
        coverSource: undefined,
      });
      addVersion(song.id, 'Cover art removed', 'cover');
      setPendingCover(null);
      showToast('Cover art removed');
    },

    /** Deletes a version and drops it from the compare selection. */
    deleteVersion(versionId: string) {
      if (!song) return;
      deleteVersionRaw(song.id, versionId);
      dropFromCompare(versionId);
      showToast('Version deleted');
    },

    /**
     * Copies a version. Free, because nothing is generated: it duplicates a record.
     *
     * Deliberately NOT routed through `request()` — that is the credit path, and putting a
     * record copy through a "this will use 2 credits" dialog would charge for an operation
     * that costs nothing to perform. Rename and delete are free for the same reason and
     * take the same shape.
     */
    duplicateVersion(versionId: string) {
      if (!song) return;
      const label = duplicateVersionRaw(song.id, versionId);
      // Only toasts on success. An unknown id produces '' and nothing happened, so
      // claiming a copy was made would be a false confirmation.
      if (label) showToast(`Copied — ${label}`);
    },

    /**
     * Loads a version as the base the edit tools act on — the retarget half of the "Load"
     * menu item. The player-cue half is player.loadVersion, called alongside this at the
     * Studio call site; together they are "Cue + retarget editing".
     *
     * Lighter than the old destructive behavior: it does NOT copy the version's audio/length/clip onto the
     * song, and it makes no backend call. It only sets `editingHeadVersionId` in
     * songsStore, which Trim/Cut/Split/Tempo/Pitch read and forward as `sourceVersionId`
     * so their next request reads THIS version's stored audio instead of the song's
     * current HEAD. Once an edit appends a new version, `replaceSong()` advances the
     * edit target to THAT latest draft version, so follow-up edits keep branching from
     * the newest result without ever promoting it onto the visible library song. Extend/
     * Remix/Replace don't honor it: they're still the client-simulated FlowMusic provider
     * path with no per-version audio to target.
     */
    loadVersionForEditing(versionId: string) {
      if (!song) return;
      const version = loadVersionForEditingRaw(song.id, versionId);
      if (version) showToast(`Loaded ${version.label}`);
    },

    async saveVersionToLibrary(versionId: string) {
      if (!song) return;
      try {
        const saved = await saveVersionToLibraryRaw(song.id, versionId);
        if (saved) {
          warmGeneratedSongForPlayback(saved);
          showToast(`Saved "${saved.title}" to your library`);
        }
      } catch (err) {
        showToast(apiErrorMessage(err, { preserveMessage: true }));
      }
    },

    /**
     * Downloads one separated stem.
     *
     * When the backend is configured this goes through the export seam and saves the real
     * stored WAV. Without a backend it falls back to the honest text placeholder.
     */
    async downloadStem(stemName: string) {
      if (!song) return;
      if (audioEditApi.supports('exportAudio')) {
        await receiveStemExport(song, stemName);
        return;
      }
      const { filename, contents } = buildStemFile(song, stemName);
      downloadTextFile(filename, contents);
      showToast(`Downloading ${stemName}`);
    },

    async playStem(stemName: string) {
      await toggleStemPlayback(stemName);
    },

    /**
     * Downloads every stem as one archive.
     *
     * Same two branches as `downloadStem` and the same gate. With a backend configured this
     * saves the real ZIP archive; without one it falls back to the honest text manifest.
     */
    async downloadAllStems() {
      if (!song) return;
      if (backendStemDownloadsAvailable) {
        await receiveStemExport(song, undefined);
        return;
      }
      const { filename, contents } = buildStemsManifest(song);
      downloadTextFile(filename, contents);
      showToast('Downloading stems ZIP');
    },
  };
}
